import { Button, Modal, Select } from 'antd';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import React, { memo, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import translations from 'translations';
import { Language, UpdateStoreBasicInfoInput, Ward } from 'types/schema';
import { convertViToEn } from 'utils/convertVnToEn';
import formatFullAddress, { LocationExt } from 'utils/formatFullAddress';
import media from 'utils/mediaStyle';
import utilMessages from 'utils/messages';
import AddressMapIcon from '../icons/AddressMapIcon.svg';

const AModal = styled(Modal)`
  .ant-modal-content {
    padding: 16px !important;
    ${media.md`
    padding: 20px 24px !important;
    `}
  }
  .ant-modal-header {
    padding-right: 10px !important;
  }
  .ant-modal-title {
    text-align: center !important;
    font-size: 16px !important;
    ${media.md`
    font-size: 20px !important;
    `}
    font-weight: 500 !important;
    line-height: 26px !important;
    color: #242527 !important;
  }
  .ant-modal-close {
    top: 14px !important;
    right: 4px !important;

    ${media.md`
    top: 18px !important;
    right: 14px !important;
    `}
  }
  .ant-modal-close-x {
    font-size: 18px !important;
  }
  .ant-select-single {
    width: 100% !important;
    height: 36px !important;
  }
  .ant-select-selection-wrap {
    font-size: 14px !important;
    line-height: 18px !important;
    color: #2f382c !important;
  }
`;

const Description = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #585a60;

  ${media.md`
    text-align: center;
  `}
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #2f382c;
  margin-top: ${(props: { marginTop?: boolean }) => (props.marginTop ? '24px' : '0px')};
`;

const Address = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #585a60;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eaebea;
  margin: 12px 0;
`;

const SelectContainer = styled.div`
  margin: 4px 0px;
  width: 100%;
`;
const ButtonContainer = styled.div`
  margin-top: 24px !important;
  display: flex;
  justify-content: center;
`;
const ConfirmButton = styled(Button)`
  width: 160px;
  height: 40px;
  font-weight: 500 !important;
  font-size: 16px !important;
  background: #43a047;
  box-shadow: none !important;
`;

interface ForceUpdateNewAddressModalProps {
  openModal: boolean;
  currentStoreAddress?: LocationExt;
  storeName: string;
  onClose: () => void;
  recommendNewWardForStore: Ward[];
  updateStoreBasicInfo: (data: Omit<UpdateStoreBasicInfoInput, 'storeUuid'>, cb?: (success: boolean) => void) => void;
  isFetchingWard?: boolean;
}

const ForceUpdateNewAddressModal = (props: ForceUpdateNewAddressModalProps) => {
  const {
    openModal,
    currentStoreAddress,
    storeName,
    onClose,
    recommendNewWardForStore,
    updateStoreBasicInfo,
    isFetchingWard,
  } = props;
  const lang = localStorage.getItem('lang') || Language.Vi;

  const [selectedWard, setSelectedWard] = useState<Ward>();
  const [loading, setLoading] = useState(false);

  const optionsData = useMemo(() => {
    const data: any = [
      {
        label: translations(utilMessages.suggested),
        title: translations(utilMessages.suggested),
        options: [],
      },
      {
        label: translations(utilMessages.all),
        title: translations(utilMessages.all),
        options: [],
      },
    ];
    recommendNewWardForStore.forEach((ward) => {
      if (ward.isRecommended) {
        data[0].options.push({ label: lang === Language.Vi ? ward.fullName : ward.fullNameEn, value: ward.code });
      } else {
        data[1].options.push({ label: lang === Language.Vi ? ward.fullName : ward.fullNameEn, value: ward.code });
      }
    });

    if (data[0].options.length === 0) {
      data.shift();
      if (data[0].options.length === 0) {
        data.pop();
      }
    } else if (data[1].options.length === 0) {
      data.pop();
    }

    return data || [];
  }, [recommendNewWardForStore]);

  const handleSaveNewWard = useCallback(() => {
    if (!selectedWard) return;
    setLoading(true);
    try {
      updateStoreBasicInfo(
        {
          ward2507Id: selectedWard.code,
        },
        (success) => {
          setLoading(false);
          if (success) {
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }
        },
      );
    } catch (error) {
      setLoading(false);
    }
  }, [selectedWard, updateStoreBasicInfo, setLoading]);

  return (
    <AModal
      destroyOnHidden={true}
      title={translations(utilMessages.postMergerAddressNotUpdated)}
      closable={false}
      open={openModal}
      footer={null}
      width={520}
      // onClose={onClose}
      // onCancel={onClose}
    >
      <SVGIcon
        src={AddressMapIcon}
        styles={{
          margin: '24px auto',
        }}
      />
      <Description>{translations(utilMessages.postMergerAdministrativeUnitChanged)}</Description>
      <Description>{translations(utilMessages.postMergerPleaseUpdateAddress)}</Description>

      <Title marginTop>{storeName}</Title>
      <Address>
        {translations(utilMessages.oldAddress)}: {formatFullAddress(currentStoreAddress)}
      </Address>
      <Divider />
      <Title>
        <span style={{ color: 'red' }}>* </span>
        {translations(utilMessages.wardPostMerger)}
      </Title>
      <SelectContainer>
        <Select
          options={optionsData}
          showSearch
          optionFilterProp="label"
          filterOption={(inputValue, option) => {
            return convertViToEn((option?.label || '') as string)?.includes(convertViToEn(inputValue)) as boolean;
          }}
          value={selectedWard?.code}
          placeholder={translations(utilMessages.search)}
          onChange={(value) => {
            const ward = recommendNewWardForStore?.find((option) => option.code === value);
            setSelectedWard(ward);
          }}
          loading={isFetchingWard}
        />
      </SelectContainer>
      {selectedWard && (
        <Address>
          {translations(utilMessages.newAddressPostMerger)}:{' '}
          {formatFullAddress({
            ...currentStoreAddress,
            ward: (lang === Language.Vi ? selectedWard.fullName : selectedWard.fullNameEn) || '',
            district: '',
          } as LocationExt)}
        </Address>
      )}

      <ButtonContainer>
        <ConfirmButton type="primary" onClick={handleSaveNewWard} disabled={!selectedWard || loading} loading={loading}>
          {translations(utilMessages.update)}
        </ConfirmButton>
      </ButtonContainer>
    </AModal>
  );
};

export default memo(ForceUpdateNewAddressModal);
